import useFeatureFlags from 'hooks/useFeatureFlags'
import type React from 'react'
import { Suspense } from 'react'
import { connect } from 'react-redux'
import type { AnyAction } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'
import {
  EMAR_ROUND_TIMINGS_V2_URL,
  loadEmarRoundTimings
} from '../../operations/EmarRoundTimings'
import Field from '../fields'
import Modal from '.'
import PopupModal from './PopupModal'

const MODAL_ID = 'addEmarRoundTimingModal'

interface EmarRoundTiming {
  time: string
  title: string
  // Define other properties of the EmarRoundTiming object
}

interface EmarRoundTimingModalProps {
  emarV2ChartEnabled?: boolean
  emarV2RoundTimings?: boolean
  isOpen: boolean
  fields: EmarRoundTiming
  setFormField: (data: any) => void // Replace 'any' with your form data type
  createItem: (data: any) => void // Replace 'any' with the expected data type
  setLoadedListItem: (list: string, item: any) => void // Replace 'any' with the expected data type
  reloadRoundTimings: () => void
  showToast: (options: { title: string }) => void
  testId?: string
  // addForm: () => void;
}

const createUrl = ({
  emarV2ChartEnabled,
  emarV2RoundTimings
}: EmarRoundTimingModalProps) => {
  if (emarV2RoundTimings) return EMAR_ROUND_TIMINGS_V2_URL
  if (emarV2ChartEnabled) return '/emar/legacy/emar-round-timings'
  return 'list/emar-round-timings'
}

const handleSubmit = (props: EmarRoundTimingModalProps) => {
  const {
    createItem,
    emarV2RoundTimings,
    fields,
    reloadRoundTimings,
    setLoadedListItem,
    showToast
  } = props

  createItem({
    json: fields,
    onComplete: (resp: any) => {
      showToast({ title: 'Added a round timing' })
      // V2 answers with the created document, not the `{ result }` envelope
      // `postItem` reads, so it hands back undefined. Refetch rather than put
      // an undefined row in the list.
      if (emarV2RoundTimings) reloadRoundTimings()
      else setLoadedListItem('emar-round-timings', resp)
    },
    url: createUrl(props)
  })
}

export const EmarRoundTimingModal: React.FC<EmarRoundTimingModalProps> = (
  props
) => {
  const flags = useFeatureFlags({
    flagNames: ['emar_v2_chart', 'emar_v2_round_timings']
  })?.data
  const emarV2ChartEnabled = flags?.emar_v2_chart?.enabled ?? false
  const emarV2RoundTimings = flags?.emar_v2_round_timings?.enabled ?? false
  const modalProps = { ...props, emarV2ChartEnabled, emarV2RoundTimings }
  const { isOpen, setFormField, testId, ...formProps } = props

  if (!isOpen) return null

  return (
    <PopupModal
      confirmText="Add"
      buttonIcon="PlusOutlined"
      buttonColour="success"
      list="Round Time"
      subtitle="You will see these when you add a medication"
      onConfirm={() => handleSubmit(modalProps)}
      data-testid={
        testId ? `${testId}-emar-round-timing-modal` : 'emar-round-timing-modal'
      }
      {...formProps}
      type="create"
    >
      <div
        style={{ display: 'flex' }}
        data-testid={
          testId
            ? `${testId}-emar-round-timing-fields`
            : 'emar-round-timing-fields'
        }
      >
        <div
          style={{ marginBottom: 20, maxWidth: 116 }}
          data-testid="emar-round-timing-time-field-container"
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              keys="time"
              label="Time"
              required
              layout="column"
              formId={MODAL_ID}
              type="time"
              values={formProps.fields}
              data-testid="emar-round-timing-time-input"
            />
          </Suspense>
        </div>
        <div
          style={{ marginBottom: 20 }}
          data-testid="emar-round-timing-name-field-container"
        >
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              keys="title"
              label="Round Name"
              layout="column"
              maxLength={13}
              placeholder="Eg. morning"
              formId={MODAL_ID}
              type="text"
              values={formProps.fields}
              data-testid="emar-round-timing-name-input"
            />
          </Suspense>
        </div>
      </div>
    </PopupModal>
  )
}

// `ThunkDispatch`, not `Dispatch`: this dispatches a thunk, not a plain action.
const mapDispatchToProps = (
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>
) => ({
  reloadRoundTimings: () =>
    dispatch(loadEmarRoundTimings({ useV2Endpoints: true }))
})

const wrappedModal = connect(null, mapDispatchToProps)(EmarRoundTimingModal)

export default Modal(wrappedModal, MODAL_ID)
